import {useEffect, useMemo, useState} from "react";
import {useLocation, useNavigate, useSearchParams} from "react-router-dom";
import {common as stringsCommon, unauthAppointments as strings} from "strings";

import AlertModal from "components/AlertModal";
import AppointmentConfirmationModal from "components/AppointmentConfirmationModal";
import Button from "components/Button";
import Divider from "components/Divider";
import LoadingIndicator from "components/LoadingIndicator";
import PageTitle from "components/PageTitle";

import {useGetAppointmentCampaigns} from "liberty-core/api/react-query/appointment";

import {useCurrentPharmacy} from "lib/useCurrentPharmacy";

import styles from "./UnauthAppointmentsPage.module.css";

type LocationState = {
  confirmDetails?: {
    startDateTime?: string;
    successMessage?: string;
    campaignName?: string;
  };
  invalidCampaignOrOptionIds?: boolean;
};

export default function UnauthAppointmentsPage() {
  const [pharmacy] = useCurrentPharmacy();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as LocationState;
  const successMessage = useMemo(
    () => locationState?.confirmDetails?.successMessage,
    [locationState],
  );
  const startDateTime = useMemo(
    () => locationState?.confirmDetails?.startDateTime,
    [locationState],
  );
  const campaignName = useMemo(
    () => locationState?.confirmDetails?.campaignName,
    [locationState],
  );
  const [
    showInvalidCampaignOrOptionIdModal,
    setShowInvalidCampaignOrOptionIdModal,
  ] = useState(!!locationState?.invalidCampaignOrOptionIds);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const {data: apptCampaigns, isLoading: isLoadingApptCampaigns} =
    useGetAppointmentCampaigns(pharmacy?.id ?? 0);
  const noApptOptions =
    !apptCampaigns || !apptCampaigns.find((c) => c.appointmentOptions?.length);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (startDateTime || successMessage) {
      setShowConfirmationModal(true);
    }
  }, [startDateTime, successMessage]);

  return (
    <div className={styles.container}>
      <PageTitle>{strings.title}</PageTitle>
      <Divider className={styles.divider} />
      <div className={styles.boxContainer}>
        <div className={styles.contentBox}>
          <div className={styles.description}>{strings.description}</div>
        </div>
        <div className={styles.contentBox}>
          <div className={styles.buttonBox}>
            {isLoadingApptCampaigns ? (
              <LoadingIndicator />
            ) : (
              <>
                <Button
                  to={`/register?${searchParams.toString()}`}
                  className={styles.buttonSpace}
                >
                  {stringsCommon.register}
                </Button>
                <Button
                  onClick={() =>
                    navigate("/appointments/guest", {
                      state: {
                        apptCampaigns:
                          apptCampaigns?.filter((c) => !c.deepLinkOnly) ?? [],
                      },
                    })
                  }
                  variant="minimal"
                  disabled={noApptOptions}
                >
                  {noApptOptions
                    ? strings.noAppointmentsAvailable
                    : strings.guest}
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
      <AppointmentConfirmationModal
        visible={showConfirmationModal}
        onClickClose={() => setShowConfirmationModal(false)}
        startDateTime={startDateTime ?? ""}
        succeessMessage={successMessage ?? ""}
        campaignName={campaignName ?? ""}
        pharmacyName={pharmacy?.name ?? ""}
      />
      <AlertModal
        title={stringsCommon.whoops}
        message={strings.invalid}
        visible={showInvalidCampaignOrOptionIdModal}
        onClickConfirm={() => setShowInvalidCampaignOrOptionIdModal(false)}
      />
    </div>
  );
}
